import React, { useEffect, useRef } from "react";

/**
 * Editeur WYSIWYG simple base sur contentEditable.
 * - Gras / italique / souligne
 * - Listes a puces / numerotees
 * - Titres H2 / H3
 * - Alignement gauche / centre / droite / justifie
 * - Lien
 *
 * Sortie : HTML brut transmis a onChange(html).
 *
 * Limitations : pas de prevention paste de styles externes. Le sanitize
 * cote backend (regex strict) protege contre tout HTML hostile et
 * normalise les alignements en classes align-left/center/right/justify.
 */
export default function RichEditor({ value, onChange, placeholder }) {
  const ref = useRef(null);
  const isUpdating = useRef(false);

  // Sync depuis prop value -> editor (au mount + quand value change de l'exterieur)
  useEffect(() => {
    if (!ref.current) return;
    if (isUpdating.current) return; // on ignore les rerenders dus a notre propre onChange
    const current = ref.current.innerHTML;
    if (current !== (value || "")) {
      ref.current.innerHTML = value || "";
    }
  }, [value]);

  const exec = (cmd, arg = null) => {
    document.execCommand(cmd, false, arg);
    ref.current && ref.current.focus();
    handleInput();
  };

  const handleInput = () => {
    if (!ref.current) return;
    isUpdating.current = true;
    onChange(ref.current.innerHTML);
    // reset flag apres le cycle React courant
    setTimeout(() => { isUpdating.current = false; }, 0);
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = (e.clipboardData || window.clipboardData).getData("text/plain");
    document.execCommand("insertText", false, text);
  };

  const handleLink = () => {
    const url = window.prompt("URL du lien :", "https://");
    if (url && url.trim()) {
      exec("createLink", url.trim());
    }
  };

  return (
    <div>
      <div className="rich-toolbar">
        <button type="button" onClick={() => exec("bold")}
                title="Gras"><strong>B</strong></button>
        <button type="button" onClick={() => exec("italic")}
                title="Italique"><em>I</em></button>
        <button type="button" onClick={() => exec("underline")}
                title="Souligné"><u>U</u></button>
        <button type="button" onClick={() => exec("formatBlock", "<h2>")}
                title="Titre">H2</button>
        <button type="button" onClick={() => exec("formatBlock", "<h3>")}
                title="Sous-titre">H3</button>
        <button type="button" onClick={() => exec("formatBlock", "<p>")}
                title="Paragraphe">¶</button>
        <button type="button" onClick={() => exec("insertUnorderedList")}
                title="Liste à puces">• Liste</button>
        <button type="button" onClick={() => exec("insertOrderedList")}
                title="Liste numérotée">1. Liste</button>
        <button type="button" onClick={() => exec("justifyLeft")}
                title="Aligner à gauche">⯇</button>
        <button type="button" onClick={() => exec("justifyCenter")}
                title="Centrer">≡</button>
        <button type="button" onClick={() => exec("justifyRight")}
                title="Aligner à droite">⯈</button>
        <button type="button" onClick={() => exec("justifyFull")}
                title="Justifier">☰</button>
        <button type="button" onClick={handleLink}
                title="Insérer un lien">🔗</button>
        <button type="button" onClick={() => exec("removeFormat")}
                title="Supprimer le formatage">Effacer</button>
      </div>
      <div
        ref={ref}
        contentEditable
        className="rich-editor"
        onInput={handleInput}
        onPaste={handlePaste}
        suppressContentEditableWarning
        data-placeholder={placeholder || ""}
      />
    </div>
  );
}
